import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FaChartLine, FaDownload, FaSpinner, FaMoneyBillWave, FaCreditCard, FaMobileAlt, FaMoneyBill, FaShoppingCart, FaTrophy, FaSearch, FaFilePdf, FaChevronDown, FaChevronRight, FaMoneyBillWaveAlt, FaFileExcel, FaUsers, FaUndo } from 'react-icons/fa';
import { MdPointOfSale } from 'react-icons/md';
import { BsCalendarDay, BsCalendarWeek, BsCalendarMonth, BsClock } from 'react-icons/bs';
import Select from 'react-select';
import { getWalkInAnalytics, getAllProductsWithSales, getProductOptionsBreakdown, downloadWalkInAnalyticsPDF, getDetailedOrdersForExcel, getWalkInReturns } from '../../services/ClientService';
import apiRequest from '../../services/apiService';
import * as XLSX from 'xlsx';

// Helper function to format currency in Indian numbering system
const formatIndianCurrency = (value) => {
  if (!value && value !== 0) return '₹0.00';

  let numValue;
  if (typeof value === 'string' && value.startsWith('₹')) {
    numValue = parseFloat(value.replace(/[₹,]/g, ''));
  } else {
    numValue = parseFloat(value);
  }

  if (isNaN(numValue)) return '₹0.00';

  if (numValue >= 100000) {
    const lakhs = (numValue / 100000).toFixed(2);
    return `₹${lakhs}L`;
  } else {
    return `₹${numValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

// HSN from product master: show code only, or NA when missing
const formatHsnForExcel = (hsn) => {
  if (hsn == null || hsn === '') return 'NA';
  const trimmed = String(hsn).trim();
  if (!trimmed || trimmed.toUpperCase() === 'N/A') return 'NA';
  return trimmed;
};

// Generate time slots from 6AM to 11PM in 30-minute intervals
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 6; hour <= 23; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    if (hour < 23) {
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
  }
  return slots;
};

// KPI Card Component
const KPICard = ({ title, value, icon: Icon, color, subtitle }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className={`bg-white rounded-xl shadow-lg p-6 border-l-4 ${color}`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600 font-medium">{title}</p>
        <h3 className="text-3xl font-bold text-gray-800 mt-2">{value}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-4 rounded-full ${color.replace('border-l', 'bg').replace('600', '100')}`}>
        <Icon className={`text-3xl ${color.replace('border-l-', 'text-')}`} />
      </div>
    </div>
  </motion.div>
);

const WalkInAnalytics = () => {
  const [timePeriod, setTimePeriod] = useState('Today');
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingFiltered, setDownloadingFiltered] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [downloadingFilteredExcel, setDownloadingFilteredExcel] = useState(false);

  // Custom date range states (Super Admin only)
  const [userRole, setUserRole] = useState(null);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [downloadingCustomExcel, setDownloadingCustomExcel] = useState(false);
  const [downloadingCustomPDF, setDownloadingCustomPDF] = useState(false);

  // GST Filing workflow states
  const [uploadedExcelData, setUploadedExcelData] = useState(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [downloadingGSTJson, setDownloadingGSTJson] = useState(false);

  // Branch selection
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchOptions, setBranchOptions] = useState([]);
  const [canSwitchBranch, setCanSwitchBranch] = useState(true);

  // KPI data
  const [kpis, setKpis] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    avgOrderValue: 0,
    totalTaxCollected: 0,
    totalItemsSold: 0,
    totalCouponDiscount: 0,
    revenueOnCash: 0,
    revenueOnCard: 0,
    revenueOnUPI: 0,
    uniqueCustomers: 0,
    ordersWithMobile: 0,
  });

  // Products data
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Expandable product options
  const [expandedProducts, setExpandedProducts] = useState({});
  const [productOptions, setProductOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState({});

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [showTimeFilter, setShowTimeFilter] = useState(false);

  // Detail tab: products | returns
  const [detailTab, setDetailTab] = useState('products'); // 'products' | 'returns'
  const [returnedItems, setReturnedItems] = useState([]);
  const [returnsLoading, setReturnsLoading] = useState(false);
  const [returnsSummary, setReturnsSummary] = useState({
    totalRefund: 0,
    totalQty: 0,
    damagedQty: 0,
    justReturnQty: 0,
    totalLines: 0,
  });
  const [returnsPagination, setReturnsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 20,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const [snackBar, setSnackBar] = useState({ show: false, message: '', type: 'success' });

  const timeSlots = generateTimeSlots();

  // Detect user role on mount
  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      if (userData) {
        setUserRole(userData.role);
      }
    } catch (error) {
      console.error('Error getting user role:', error);
    }
  }, []);

  // Check if user is super admin or master admin
  const isSuperOrMasterAdmin = userRole === 1 || userRole === 2;

  // Fetch branches on mount
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await apiRequest('/clients/getBranchNamesForUser', 'POST', {});

        console.log('Branch fetch response:', response); // Debug log

        if (response.success && response.data) {
          const canSwitch = response.canSwitchBranch !== false;

          // If user can't switch branches, show only their branch name as text
          if (!canSwitch && response.data.length === 1) {
            const branch = response.data[0];
            setSelectedBranch({
              value: branch._id,
              label: branch.branchName
            });
            setBranchOptions([{
              value: branch._id,
              label: branch.branchName
            }]);
            setCanSwitchBranch(false);
          } else {
            // User can switch branches - show dropdown
            setCanSwitchBranch(true);

            // Create options array starting with "All Branches"
            const options = [
              { value: 'all', label: 'All Branches (Master View)' }
            ];

            // Add all branches (excluding master account/warehouse)
            const branchOptions = response.data
              .filter(branch => !branch.masterAccount && !branch.isWarehouse)
              .map(branch => ({
                value: branch._id,
                label: branch.branchName
              }));

            options.push(...branchOptions);
            setBranchOptions(options);

            // Set default to "All Branches"
            setSelectedBranch(options[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };

    fetchBranches();
  }, []);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const showSnackBar = useCallback((message, type = 'success') => {
    setSnackBar({ show: true, message, type });
    setTimeout(() => setSnackBar({ show: false, message: '', type: 'success' }), 3000);
  }, []);

  const fetchKPIs = useCallback(async () => {
    setLoading(true);
    try {
      const requestData = {
        timePeriod,
        selectedBranchId: selectedBranch?.value || null
      };

      const response = await getWalkInAnalytics(requestData);

      if (response.success) {
        setKpis(response.data.kpis);
      } else {
        showSnackBar(response.message || 'Failed to fetch analytics', 'error');
      }
    } catch (error) {
      console.error('Analytics fetch error:', error);
      showSnackBar('Error fetching analytics. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [timePeriod, selectedBranch, showSnackBar]);

  const fetchProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const requestData = {
        timePeriod,
        page: pagination.currentPage,
        limit: 10,
        search: debouncedSearchTerm,
        selectedBranchId: selectedBranch?.value || null
      };

      // Add time filters only if they are set
      if (startTime && endTime) {
        requestData.startTime = startTime;
        requestData.endTime = endTime;
      }

      const response = await getAllProductsWithSales(requestData);

      if (response.success) {
        setProducts(response.data.products);
        setPagination(response.data.pagination);
      } else {
        showSnackBar(response.message || 'Failed to fetch products', 'error');
      }
    } catch (error) {
      console.error('Products fetch error:', error);
      showSnackBar('Error fetching products. Please try again.', 'error');
    } finally {
      setProductsLoading(false);
    }
  }, [
    timePeriod,
    pagination.currentPage,
    debouncedSearchTerm,
    startTime,
    endTime,
    selectedBranch,
    showSnackBar,
  ]);

  const fetchReturns = useCallback(async () => {
    setReturnsLoading(true);
    try {
      const requestData = {
        timePeriod,
        page: returnsPagination.currentPage,
        limit: 20,
        search: debouncedSearchTerm,
        selectedBranchId: selectedBranch?.value || null,
      };

      if (startTime && endTime) {
        requestData.startTime = startTime;
        requestData.endTime = endTime;
      }

      if (timePeriod === 'Custom' && customStartDate && customEndDate) {
        requestData.customStartDate = customStartDate;
        requestData.customEndDate = customEndDate;
      }

      const response = await getWalkInReturns(requestData);

      if (response.success) {
        setReturnedItems(response.data.returns || []);
        setReturnsSummary(response.data.summary || {
          totalRefund: 0,
          totalQty: 0,
          damagedQty: 0,
          justReturnQty: 0,
          totalLines: 0,
        });
        setReturnsPagination(response.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          limit: 20,
          hasNextPage: false,
          hasPrevPage: false,
        });
      } else {
        showSnackBar(response.message || 'Failed to fetch returns', 'error');
      }
    } catch (error) {
      console.error('Returns fetch error:', error);
      showSnackBar('Error fetching returns. Please try again.', 'error');
    } finally {
      setReturnsLoading(false);
    }
  }, [
    timePeriod,
    returnsPagination.currentPage,
    debouncedSearchTerm,
    startTime,
    endTime,
    selectedBranch,
    customStartDate,
    customEndDate,
    showSnackBar,
  ]);

  useEffect(() => {
    fetchKPIs();
  }, [fetchKPIs]);

  useEffect(() => {
    if (detailTab === 'products') {
      fetchProducts();
    }
  }, [detailTab, fetchProducts]);

  useEffect(() => {
    if (detailTab === 'returns') {
      fetchReturns();
    }
  }, [detailTab, fetchReturns]);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      await downloadWalkInAnalyticsPDF({
        timePeriod,
        selectedBranchId: selectedBranch?.value || null
      });
      showSnackBar('PDF downloaded successfully!', 'success');
    } catch (error) {
      console.error('PDF download error:', error);
      showSnackBar('Failed to download PDF. Please try again.', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadFilteredPDF = async () => {
    if (!startTime || !endTime) {
      showSnackBar('Please select both start and end time', 'error');
      return;
    }

    setDownloadingFiltered(true);
    try {
      await downloadWalkInAnalyticsPDF({
        timePeriod,
        startTime,
        endTime,
        selectedBranchId: selectedBranch?.value || null
      });
      showSnackBar('Filtered PDF downloaded successfully!', 'success');
    } catch (error) {
      console.error('Filtered PDF download error:', error);
      showSnackBar('Failed to download filtered PDF. Please try again.', 'error');
    } finally {
      setDownloadingFiltered(false);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      await downloadExcelReport({
        timePeriod,
        selectedBranchId: selectedBranch?.value || null
      });
      showSnackBar('Excel downloaded successfully!', 'success');
    } catch (error) {
      console.error('Excel download error:', error);
      showSnackBar('Failed to download Excel. Please try again.', 'error');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleDownloadFilteredExcel = async () => {
    if (!startTime || !endTime) {
      showSnackBar('Please select both start and end time', 'error');
      return;
    }

    setDownloadingFilteredExcel(true);
    try {
      await downloadExcelReport({
        timePeriod,
        startTime,
        endTime,
        selectedBranchId: selectedBranch?.value || null
      });
      showSnackBar('Filtered Excel downloaded successfully!', 'success');
    } catch (error) {
      console.error('Filtered Excel download error:', error);
      showSnackBar('Failed to download filtered Excel. Please try again.', 'error');
    } finally {
      setDownloadingFilteredExcel(false);
    }
  };

  // Custom date range Excel download (Super Admin only)
  const handleCustomDateExcelDownload = async () => {
    if (!customStartDate || !customEndDate) {
      showSnackBar('Please select both start and end date', 'error');
      return;
    }
    if (new Date(customEndDate) < new Date(customStartDate)) {
      showSnackBar('End date must be after start date', 'error');
      return;
    }

    setDownloadingCustomExcel(true);
    try {
      await downloadExcelReport({
        timePeriod: 'Custom',
        customStartDate,
        customEndDate,
        selectedBranchId: selectedBranch?.value || null
      });
      showSnackBar('Custom range Excel downloaded successfully!', 'success');
    } catch (error) {
      console.error('Custom Excel download error:', error);
      showSnackBar('Failed to download Excel. Please try again.', 'error');
    } finally {
      setDownloadingCustomExcel(false);
    }
  };

  // Custom date range PDF download (Super Admin only)
  const handleCustomDatePDFDownload = async () => {
    if (!customStartDate || !customEndDate) {
      showSnackBar('Please select both start and end date', 'error');
      return;
    }
    if (new Date(customEndDate) < new Date(customStartDate)) {
      showSnackBar('End date must be after start date', 'error');
      return;
    }

    setDownloadingCustomPDF(true);
    try {
      await downloadWalkInAnalyticsPDF({
        timePeriod: 'Custom',
        customStartDate,
        customEndDate,
        selectedBranchId: selectedBranch?.value || null
      });
      showSnackBar('Custom range PDF downloaded successfully!', 'success');
    } catch (error) {
      console.error('Custom PDF download error:', error);
      showSnackBar('Failed to download PDF. Please try again.', 'error');
    } finally {
      setDownloadingCustomPDF(false);
    }
  };

  // Excel export function with Tally-compatible format
  const downloadExcelReport = async (params) => {
    try {
      // Fetch detailed order data
      const response = await getDetailedOrdersForExcel(params);

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch data');
      }

      const { orders, summary } = response.data;

      // ==================== PRE-PROCESS: Flatten all items into a single array ====================
      // This ensures Item Wise Details, HSN Summary, and all totals use the EXACT same data source
      const allItems = [];
      orders.forEach(order => {
        (order.items || []).forEach(item => {
          allItems.push({
            // All item-level fields
            productName: item.productName || 'N/A',
            productId: item.productId,
            hsnCode: formatHsnForExcel(item.hsnCode),
            quantity: item.quantity,
            baseQuantity: item.baseQuantity || 1,
            unit: item.unit || 'PCS',
            quantityDescription: item.quantityDescription,
            isCommonInventory: item.isCommonInventory || false,
            conversionFactor: item.conversionFactor || 1,
            primaryMetric: item.primaryMetric,
            unitPrice: item.unitPrice || 0,
            originalPrice: item.originalPrice || 0,
            discount: item.discount || 0,
            discountAmount: item.discountAmount || 0,
            taxableAmount: item.taxableAmount || 0,
            gstRate: item.gstRate || 0,
            gstType: item.gstType,
            cgst: item.cgst || 0,
            sgst: item.sgst || 0,
            igst: item.igst || 0,
            totalTax: item.totalTax || 0,
            isInterState: item.isInterState || false,
            itemTotal: item.itemTotal || 0,
            // Item-level cancellation flag (partial item cancellation)
            _itemCancelled: item.isCancelled || false,
            // Order-level metadata (prefixed with _ to avoid conflicts)
            _orderType: order.orderType,
            _isCancelled: order.isCancelled || false,
            _status: order.status,
            _hasPartialCancellation: order.hasPartialCancellation || false,
            _invoiceNumber: order.invoiceNumber,
            _orderDate: order.orderDate,
            _orderTime: order.orderTime,
            _branchName: order.branchName,
            _branchGSTIN: order.branchGSTIN,
            _customerName: order.customerName,
            _customerPhone: order.customerPhone,
            _paymentMethod: order.paymentMethod,
            _finalAmount: order.finalAmount,
            _deliveryCharges: order.deliveryCharges || 0,
            _roundOff: order.roundOff,
            _salesPerson: order.salesPerson
          });
        });
      });

      // Pre-compute item-level totals for consistency across all sheets
      // Active items: exclude BOTH order-level cancelled AND item-level cancelled items
      const activeItems = allItems.filter(i => !i._isCancelled && !i._itemCancelled);
      const itemLevelTotal = activeItems.reduce((sum, i) => sum + i.itemTotal, 0);
      const itemLevelTaxable = activeItems.reduce((sum, i) => sum + i.taxableAmount, 0);
      const itemLevelTax = activeItems.reduce((sum, i) => sum + i.totalTax, 0);

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Count partially cancelled orders and item-level cancelled items
      const partialCancelledOrders = orders.filter(o => o.hasPartialCancellation && !o.isCancelled).length;
      const itemCancelledCount = allItems.filter(i => i._itemCancelled && !i._isCancelled).length;

      // ==================== SHEET 1: SUMMARY ====================
      const summaryData = [
        ['SALES REPORT - DAY END SUMMARY'],
        [''],
        ['Report Details'],
        ['Branch', summary.branchLabel],
        ['Period', summary.timePeriod],
        ['Date Range', `${new Date(summary.dateRange.start).toLocaleString()} to ${new Date(summary.dateRange.end).toLocaleString()}`],
        ['Generated On', new Date().toLocaleString()],
        [''],
        ['Order Summary'],
        ['Total Orders', summary.totalOrders],
        ['Walk-in Orders', summary.walkInOrders || 0],
        ['Online Orders', summary.onlineOrders || 0],
        ['Delivered Orders', summary.deliveredOrders || 0],
        ['Cancelled Orders (Full)', summary.cancelledOrders || 0],
        ['Partially Cancelled Orders', partialCancelledOrders],
        ['Items Cancelled (Partial)', itemCancelledCount],
        [''],
        ['Financial Summary (Excluding Cancelled Items)'],
        ['Total Revenue (Order Level)', `₹${summary.totalRevenue.toFixed(2)}`],
        ['Total Item Value', `₹${itemLevelTotal.toFixed(2)}`],
        ['Total Taxable Value', `₹${itemLevelTaxable.toFixed(2)}`],
        ['Total Tax Collected', `₹${itemLevelTax.toFixed(2)}`],
        ['Total Coupon Discount', `₹${summary.totalCouponDiscount.toFixed(2)}`],
        ['Cancelled Order Value', `₹${(summary.cancelledRevenue || 0).toFixed(2)}`],
        [''],
        ['Payment Method Breakdown (Excluding Cancelled)'],
        ['Cash Revenue', `₹${summary.cashRevenue.toFixed(2)}`],
        ['Card Revenue', `₹${summary.cardRevenue.toFixed(2)}`],
        ['UPI Revenue', `₹${summary.upiRevenue.toFixed(2)}`],
      ];

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);

      // Set column widths for summary sheet
      summarySheet['!cols'] = [
        { wch: 30 },
        { wch: 40 }
      ];

      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

      // ==================== SHEET 2: INVOICE WISE (Tally Import Format) ====================
      const tallyHeaders = [
        'Order Type',
        'Status',
        'Invoice No',
        'Date',
        'Time',
        'Branch Name',
        'Branch GSTIN',
        'Customer Name',
        'Customer Phone',
        'Payment Method',
        'Subtotal',
        'Total Discount',
        'Coupon Discount',
        'Taxable Amount',
        'CGST (₹)',
        'SGST (₹)',
        'IGST (₹)',
        'Total Tax',
        'Final Amount',
        'Delivery Charges',
        'Final Amount (Excl. Delivery)',
        'Round Off',
        'Sales Person'
      ];

      const tallyData = [tallyHeaders];

      orders.forEach(order => {
        // Calculate totals from items - exclude item-level cancelled items for accurate tax
        const activeOrderItems = (order.items || []).filter(item => !item.isCancelled);
        const totalCGST = activeOrderItems.reduce((sum, item) => sum + (item.cgst || 0), 0);
        const totalSGST = activeOrderItems.reduce((sum, item) => sum + (item.sgst || 0), 0);
        const totalIGST = activeOrderItems.reduce((sum, item) => sum + (item.igst || 0), 0);
        const taxableAmount = activeOrderItems.reduce((sum, item) => sum + (item.taxableAmount || 0), 0);

        // Final amount excluding delivery charges
        const deliveryCharges = order.deliveryCharges || 0;
        const finalAmountExclDelivery = parseFloat((order.finalAmount - deliveryCharges).toFixed(2));

        // Determine status
        let status;
        if (order.isCancelled) {
          status = 'Cancelled';
        } else if (order.hasPartialCancellation) {
          status = 'Partially Cancelled';
        } else {
          status = order.status || 'Active';
        }

        tallyData.push([
          order.orderType,
          status,
          order.invoiceNumber,
          order.orderDate,
          order.orderTime,
          order.branchName,
          order.branchGSTIN,
          order.customerName,
          order.customerPhone,
          order.paymentMethod,
          order.subtotal,
          order.totalDiscount,
          order.couponDiscount,
          taxableAmount.toFixed(2),
          totalCGST.toFixed(2),
          totalSGST.toFixed(2),
          totalIGST.toFixed(2),
          order.totalTax,
          deliveryCharges,
          finalAmountExclDelivery,
          order.finalAmount,
          order.roundOff,
          order.salesPerson
        ]);
      });

      const tallySheet = XLSX.utils.aoa_to_sheet(tallyData);

      // Set column widths for better readability
      tallySheet['!cols'] = [
        { wch: 15 }, // Order Type
        { wch: 18 }, // Status
        { wch: 15 }, // Invoice No
        { wch: 12 }, // Date
        { wch: 10 }, // Time
        { wch: 20 }, // Branch Name
        { wch: 18 }, // GSTIN
        { wch: 20 }, // Customer Name
        { wch: 15 }, // Phone
        { wch: 12 }, // Payment
        { wch: 12 }, // Subtotal
        { wch: 12 }, // Discount
        { wch: 12 }, // Coupon Disc
        { wch: 12 }, // Taxable
        { wch: 10 }, // CGST
        { wch: 10 }, // SGST
        { wch: 10 }, // IGST
        { wch: 12 }, // Total Tax
        { wch: 15 }, // Delivery Charges
        { wch: 28 }, // Final Amount (Excl. Delivery)
        { wch: 12 }, // Final Amount
        { wch: 10 }, // Round Off
        { wch: 15 }  // Sales Person
      ];

      XLSX.utils.book_append_sheet(workbook, tallySheet, 'GSTR-1 Summary(B2C)');

      // ==================== SHEET 3: ITEM WISE DETAILS (For Product Analysis) ====================
      const itemHeaders = [
        'Order Type',
        'Status',
        'Invoice No',
        'Date',
        'Time',
        'Branch',
        'Product Name',
        'HSN/SAC Code',
        'Weight/Qty per Unit',
        'Unit',
        'Units Ordered',
        'Total Quantity',
        'Unit Price',
        'Original Price',
        'Discount %',
        'Discount Amount',
        'Taxable Amount',
        'GST Rate %',
        'CGST (₹)',
        'SGST (₹)',
        'IGST (₹)',
        'Total Tax',
        'Item Total',
        'Payment Method'
      ];

      const itemData = [itemHeaders];

      // Use the shared allItems array to guarantee consistency with HSN Summary
      allItems.forEach(item => {
        const weightPerUnit = item.isCommonInventory ? item.quantity : '-';
        const unitsOrdered = item.baseQuantity;
        const totalQuantity = item.isCommonInventory
          ? `${(item.quantity * item.baseQuantity).toFixed(2)} ${item.unit}`
          : `${item.baseQuantity} ${item.unit}`;

        // Determine item-level status:
        // 1. Order fully cancelled → "Cancelled"
        // 2. Individual item cancelled (partial cancellation) → "Item Cancelled"
        // 3. Active → show order status
        let itemStatus;
        if (item._isCancelled) {
          itemStatus = 'Cancelled';
        } else if (item._itemCancelled) {
          itemStatus = 'Item Cancelled';
        } else {
          itemStatus = item._status || 'Active';
        }

        itemData.push([
          item._orderType,
          itemStatus,
          item._invoiceNumber,
          item._orderDate,
          item._orderTime,
          item._branchName,
          item.productName,
          item.hsnCode,
          weightPerUnit,
          item.unit,
          unitsOrdered,
          totalQuantity,
          item.unitPrice,
          item.originalPrice,
          item.discount,
          item.discountAmount,
          item.taxableAmount,
          item.gstRate,
          item.cgst,
          item.sgst,
          item.igst,
          item.totalTax,
          item.itemTotal,
          item._paymentMethod
        ]);
      });

      const itemSheet = XLSX.utils.aoa_to_sheet(itemData);

      // Set column widths
      itemSheet['!cols'] = [
        { wch: 15 }, // Order Type
        { wch: 12 }, // Status
        { wch: 15 }, // Invoice No
        { wch: 12 }, // Date
        { wch: 10 }, // Time
        { wch: 20 }, // Branch
        { wch: 30 }, // Product Name
        { wch: 12 }, // HSN
        { wch: 15 }, // Weight/Qty per Unit
        { wch: 8 },  // Unit
        { wch: 12 }, // Units Ordered
        { wch: 15 }, // Total Quantity
        { wch: 12 }, // Unit Price
        { wch: 12 }, // Original Price
        { wch: 10 }, // Discount %
        { wch: 12 }, // Discount Amount
        { wch: 12 }, // Taxable Amount
        { wch: 10 }, // GST Rate
        { wch: 10 }, // CGST
        { wch: 10 }, // SGST
        { wch: 10 }, // IGST
        { wch: 10 }, // Total Tax
        { wch: 12 }, // Item Total
        { wch: 12 }  // Payment Method
      ];

      XLSX.utils.book_append_sheet(workbook, itemSheet, 'Item Wise Details');

      // ==================== SHEET 4: GST SUMMARY (For Tax Filing) ====================
      const gstSummaryHeaders = [
        'HSN/SAC Code',
        'Product Name',
        'Total Quantity',
        'Unit',
        'Taxable Val (₹)',
        'GST Rate %',
        'CGST Amount (₹)',
        'SGST Amount (₹)',
        'IGST Amount (₹)',
        'Total Tax',
        'Total Value'
      ];

      // Group by HSN code + Product Name + GST Rate (separate row for each product)
      // IMPORTANT: Only include non-cancelled orders in HSN Summary for accurate tax reporting
      // Uses the same allItems array as Item Wise Details to guarantee consistent totals
      const gstGrouping = {};

      activeItems.forEach(item => {
        const key = `${item.hsnCode}_${item.productName}_${item.gstRate}`;

        if (!gstGrouping[key]) {
          gstGrouping[key] = {
            hsnCode: item.hsnCode,
            productName: item.productName,
            isCommonInventory: item.isCommonInventory,
            weightData: [], // For weight-based: { value, unit, conversionFactor }
            primaryMetric: null, // Store primary metric (Kg, L, etc.)
            regularCount: 0, // For regular products: simple count
            taxableValue: 0,
            gstRate: item.gstRate,
            cgst: 0,
            sgst: 0,
            igst: 0,
            totalTax: 0,
            totalValue: 0
          };
        }

        // Handle quantity based on product type
        if (item.isCommonInventory) {
          // For weight-based products, store weight data with conversion info from backend
          const totalWeight = item.quantity * item.baseQuantity;

          // Store the first primaryMetric we encounter for this product
          if (!gstGrouping[key].primaryMetric) {
            gstGrouping[key].primaryMetric = item.primaryMetric || item.unit;
          }

          gstGrouping[key].weightData.push({
            value: totalWeight,
            unit: item.unit,
            conversionFactor: item.conversionFactor || 1 // Use actual conversionFactor from backend
          });
        } else {
          // For regular products, just sum the count
          gstGrouping[key].regularCount += item.baseQuantity;
        }

        gstGrouping[key].taxableValue += item.taxableAmount;
        gstGrouping[key].cgst += item.cgst;
        gstGrouping[key].sgst += item.sgst;
        gstGrouping[key].igst += item.igst;
        gstGrouping[key].totalTax += item.totalTax;
        gstGrouping[key].totalValue += item.itemTotal;
      });

      const gstSummaryData = [gstSummaryHeaders];

      Object.values(gstGrouping).forEach(group => {
        let quantityValue;
        let quantityUnit;

        if (group.isCommonInventory) {
          // For weight-based products: convert all to primary metric and sum
          let totalInPrimaryMetric = 0;

          group.weightData.forEach(w => {
            // Convert to primary metric using conversionFactor
            // ml (0.001): 1500 ml * 0.001 = 1.5 L
            // L (1): 3 L * 1 = 3 L
            // gm (0.001): 400 gm * 0.001 = 0.4 Kg
            // Kg (1): 1 Kg * 1 = 1 Kg
            totalInPrimaryMetric += w.value * w.conversionFactor;
          });

          // Separate number and unit
          quantityValue = totalInPrimaryMetric.toFixed(2);
          quantityUnit = group.primaryMetric || 'unit';
        } else {
          // For regular products: just show the count
          quantityValue = group.regularCount;
          quantityUnit = 'Unit';
        }

        gstSummaryData.push([
          group.hsnCode,
          group.productName,
          quantityValue,
          quantityUnit,
          group.taxableValue.toFixed(2),
          group.gstRate,
          group.cgst.toFixed(2),
          group.sgst.toFixed(2),
          group.igst.toFixed(2),
          group.totalTax.toFixed(2),
          group.totalValue.toFixed(2)
        ]);
      });

      // Add totals row
      const totals = Object.values(gstGrouping).reduce((acc, group) => ({
        taxableValue: acc.taxableValue + group.taxableValue,
        cgst: acc.cgst + group.cgst,
        sgst: acc.sgst + group.sgst,
        igst: acc.igst + group.igst,
        totalTax: acc.totalTax + group.totalTax,
        totalValue: acc.totalValue + group.totalValue
      }), { taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalTax: 0, totalValue: 0 });

      gstSummaryData.push(['']);
      gstSummaryData.push([
        'TOTAL',
        '',
        '', // No total quantity (mix of units and counts)
        '', // No unit for total
        totals.taxableValue.toFixed(2),
        '',
        totals.cgst.toFixed(2),
        totals.sgst.toFixed(2),
        totals.igst.toFixed(2),
        totals.totalTax.toFixed(2),
        totals.totalValue.toFixed(2)
      ]);

      const gstSheet = XLSX.utils.aoa_to_sheet(gstSummaryData);
      gstSheet['!cols'] = [
        { wch: 15 }, // HSN/SAC Code
        { wch: 30 }, // Product Name
        { wch: 12 }, // Total Quantity
        { wch: 8 },  // Unit
        { wch: 15 }, // Taxable Val
        { wch: 12 }, // GST Rate
        { wch: 12 }, // CGST
        { wch: 12 }, // SGST
        { wch: 12 }, // IGST
        { wch: 12 }, // Total Tax
        { wch: 15 }  // Total Value
      ];

      XLSX.utils.book_append_sheet(workbook, gstSheet, 'HSN Summary');

      // ==================== SHEET 5: DOCUMENT SUMMARY ====================
      const docSummaryHeaders = [
        'Nature of Document',
        'From',
        'To',
        'Voucher Count',
        'Cancelled',
        'Partially Cancelled',
        'Net Issued'
      ];

      // Separate online and walk-in orders
      const walkInOrders = orders.filter(o => o.orderType === 'Walk-in Order');
      const onlineOrders = orders.filter(o => o.orderType === 'Online Order');

      const docSummaryData = [docSummaryHeaders];

      // Function to get invoice number range and counts
      const getInvoiceSummary = (ordersList, label) => {
        if (ordersList.length === 0) {
          return [label, '-', '-', 0, 0, 0, 0];
        }

        // Extract invoice numbers and sort them
        const invoiceNumbers = ordersList.map(o => o.invoiceNumber).sort();
        const fromInvoice = invoiceNumbers[0];
        const toInvoice = invoiceNumbers[invoiceNumbers.length - 1];

        // Calculate voucher count (total orders)
        const voucherCount = ordersList.length;

        // Count fully cancelled orders
        const cancelledCount = ordersList.filter(o => o.isCancelled === true).length;

        // Count partially cancelled orders (has item cancellation but order not fully cancelled)
        const partiallyCancelledCount = ordersList.filter(o => o.hasPartialCancellation && !o.isCancelled).length;

        // Net issued = voucher count - fully cancelled
        const netIssued = voucherCount - cancelledCount;

        return [label, fromInvoice, toInvoice, voucherCount, cancelledCount, partiallyCancelledCount, netIssued];
      };

      //Always show both Walk-in and Online rows
      docSummaryData.push(getInvoiceSummary(walkInOrders, 'Walk-in Sales'));
      docSummaryData.push(getInvoiceSummary(onlineOrders, 'Online Sales'));

      // Add empty row then totals
      const totalVouchers = walkInOrders.length + onlineOrders.length;
      const totalCancelled = walkInOrders.filter(o => o.isCancelled).length + onlineOrders.filter(o => o.isCancelled).length;
      const totalPartiallyCancelled = walkInOrders.filter(o => o.hasPartialCancellation && !o.isCancelled).length + onlineOrders.filter(o => o.hasPartialCancellation && !o.isCancelled).length;
      const totalNetIssued = totalVouchers - totalCancelled;

      docSummaryData.push(['']);
      docSummaryData.push([
        'TOTAL',
        '',
        '',
        totalVouchers,
        totalCancelled,
        totalPartiallyCancelled,
        totalNetIssued
      ]);

      const docSummarySheet = XLSX.utils.aoa_to_sheet(docSummaryData);
      docSummarySheet['!cols'] = [
        { wch: 25 }, // Nature of Document
        { wch: 15 }, // From
        { wch: 15 }, // To
        { wch: 15 }, // Voucher Count
        { wch: 12 }, // Cancelled
        { wch: 20 }, // Partially Cancelled
        { wch: 12 }  // Net Issued
      ];

      XLSX.utils.book_append_sheet(workbook, docSummarySheet, 'Document Summary');

      // Generate filename
      let filename = `Walk-In-DayEnd-${summary.timePeriod}-${summary.branchLabel.replace(/[^a-zA-Z0-9]/g, '_')}`;
      if (params.startTime && params.endTime) {
        filename += `-${params.startTime.replace(':', '')}-${params.endTime.replace(':', '')}`;
      }
      if (params.customStartDate && params.customEndDate) {
        filename += `-${params.customStartDate}-to-${params.customEndDate}`;
      }
      filename += `-${new Date().toISOString().split('T')[0]}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);

    } catch (error) {
      console.error('Excel generation error:', error);
      throw error;
    }
  };

  // Handle Excel file upload for GST filing
  const handleExcelUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingExcel(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);

      // Parse the uploaded Excel file
      const parsedData = {
        summary: null,
        b2cInvoices: [],
        hsnSummary: [],
        documentSummary: null
      };

      // Read Summary sheet
      if (workbook.SheetNames.includes('Summary')) {
        const summarySheet = workbook.Sheets['Summary'];
        const summaryData = XLSX.utils.sheet_to_json(summarySheet, { header: 1 });
        parsedData.summary = summaryData;
      }

      // Read GSTR-1 Summary(B2C) sheet
      if (workbook.SheetNames.includes('GSTR-1 Summary(B2C)')) {
        const b2cSheet = workbook.Sheets['GSTR-1 Summary(B2C)'];
        const b2cData = XLSX.utils.sheet_to_json(b2cSheet);
        parsedData.b2cInvoices = b2cData;
      }

      // Read HSN Summary sheet
      if (workbook.SheetNames.includes('HSN Summary')) {
        const hsnSheet = workbook.Sheets['HSN Summary'];
        const hsnData = XLSX.utils.sheet_to_json(hsnSheet);
        parsedData.hsnSummary = hsnData;
      }

      // Read Document Summary sheet
      if (workbook.SheetNames.includes('Document Summary')) {
        const docSheet = workbook.Sheets['Document Summary'];
        const docData = XLSX.utils.sheet_to_json(docSheet);
        parsedData.documentSummary = docData;
      }

      setUploadedExcelData(parsedData);
      showSnackBar('Excel file uploaded successfully! You can now download GST JSON.', 'success');
    } catch (error) {
      console.error('Excel upload error:', error);
      showSnackBar('Error reading Excel file. Please ensure it\'s in the correct format.', 'error');
    } finally {
      setUploadingExcel(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Generate and download GST-compliant JSON for portal filing
  const downloadGSTJson = () => {
    if (!uploadedExcelData) {
      showSnackBar('Please upload an Excel file first', 'error');
      return;
    }

    setDownloadingGSTJson(true);
    try {
      // Generate GSTR-1 compliant JSON structure
      const gstr1Json = {
        gstin: "", // To be filled by user
        fp: "", // Financial period (MMYYYY) - to be filled by user
        version: "GST3.0.4",
        hash: "hash",

        // B2C Small (Less than Rs 2.5 Lakh) - Invoice-wise
        b2cs: uploadedExcelData.hsnSummary
          .filter(item => item['HSN/SAC Code'] && item['HSN/SAC Code'] !== 'TOTAL')
          .map(item => ({
            sply_ty: "INTRA", // Supply type - modify based on your state
            pos: "37", // Place of supply - modify based on state code
            typ: "OE", // Type: OE = Ecommerce, E = Exports
            txval: parseFloat(item['Taxable Val (₹)'] || 0),
            rt: parseFloat(item['GST Rate %'] || 0),
            iamt: parseFloat(item['IGST Amount (₹)'] || 0),
            camt: parseFloat(item['CGST Amount (₹)'] || 0),
            samt: parseFloat(item['SGST Amount (₹)'] || 0),
            csamt: 0 // Cess amount
          })),

        // HSN Summary for goods
        hsn: {
          data: uploadedExcelData.hsnSummary
            .filter(item => item['HSN/SAC Code'] && item['HSN/SAC Code'] !== 'TOTAL')
            .map(item => ({
              num: 1, // Serial number
              hsn_sc: item['HSN/SAC Code'] || '',
              desc: item['Product Name'] || '',
              uqc: item['Unit'] === 'Kg' ? 'KGS' : item['Unit'] === 'L' ? 'LTR' : 'UNT', // Unit Quantity Code
              qty: parseFloat(item['Total Quantity'] || 0),
              val: parseFloat(item['Taxable Val (₹)'] || 0),
              txval: parseFloat(item['Taxable Val (₹)'] || 0),
              iamt: parseFloat(item['IGST Amount (₹)'] || 0),
              camt: parseFloat(item['CGST Amount (₹)'] || 0),
              samt: parseFloat(item['SGST Amount (₹)'] || 0),
              csamt: 0
            }))
        },

        // Document issued summary
        doc_issue: {
          doc_det: uploadedExcelData.documentSummary
            ? uploadedExcelData.documentSummary
              .filter(item => item['Nature of Document'] && item['Nature of Document'] !== 'TOTAL')
              .map(item => ({
                doc_num: 1,
                docs: {
                  num: item['Voucher Count'] || 0,
                  from: item['From'] || '',
                  to: item['To'] || '',
                  totnum: item['Voucher Count'] || 0,
                  cancel: item['Cancelled'] || 0,
                  net_issue: item['Net Issued'] || 0
                },
                doc_typ: item['Nature of Document']?.includes('Walk-in') ? 'Invoices for outward supply' : 'Invoices for outward supply'
              }))
            : []
        }
      };

      // Create JSON blob and download
      const jsonString = JSON.stringify(gstr1Json, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const filename = `GSTR1_${new Date().toISOString().split('T')[0]}.json`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSnackBar('GST JSON downloaded successfully!', 'success');
    } catch (error) {
      console.error('GST JSON generation error:', error);
      showSnackBar('Error generating GST JSON. Please check the uploaded file.', 'error');
    } finally {
      setDownloadingGSTJson(false);
    }
  };

  const handleTimePeriodChange = (event) => {
    const newPeriod = event.target.value;
    setTimePeriod(newPeriod);
    setPagination({ ...pagination, currentPage: 1 });
    setReturnsPagination((prev) => ({ ...prev, currentPage: 1 }));

    // Clear cached product options when time period changes
    setProductOptions({});
    setExpandedProducts({});

    // Reset time filters when switching periods (only allow time filters for 'Today')
    if (newPeriod === 'Weekly' || newPeriod === 'Monthly') {
      setStartTime('');
      setEndTime('');
      setShowTimeFilter(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPagination({ ...pagination, currentPage: 1 }); // Reset to first page on search
    setReturnsPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, currentPage: newPage });
  };

  const handleReturnsPageChange = (newPage) => {
    setReturnsPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  const handleDetailTabChange = (tab) => {
    setDetailTab(tab);
    setSearchTerm('');
    setDebouncedSearchTerm('');
    if (tab === 'products') {
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    } else {
      setReturnsPagination((prev) => ({ ...prev, currentPage: 1 }));
    }
  };

  const handleTimeFilterToggle = () => {
    if (showTimeFilter) {
      // Clear filters when hiding
      setStartTime('');
      setEndTime('');
    }
    setShowTimeFilter(!showTimeFilter);
  };

  const handleApplyTimeFilter = () => {
    if (!startTime || !endTime) {
      showSnackBar('Please select both start and end time', 'error');
      return;
    }

    // Validate that end time is after start time
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    if (endMinutes <= startMinutes) {
      showSnackBar('End time must be after start time', 'error');
      return;
    }

    // Clear cached product options when time filter changes
    setProductOptions({});
    setExpandedProducts({});

    setPagination({ ...pagination, currentPage: 1 });
    fetchProducts();
  };

  const handleClearTimeFilter = () => {
    setStartTime('');
    setEndTime('');

    // Clear cached product options when time filter is cleared
    setProductOptions({});
    setExpandedProducts({});

    setPagination({ ...pagination, currentPage: 1 });
  };

  const handleProductClick = async (product) => {
    const productKey = product.productId;

    // Toggle expansion
    if (expandedProducts[productKey]) {
      setExpandedProducts({ ...expandedProducts, [productKey]: false });
      return;
    }

    // If not expanded and we don't have the data yet, fetch it
    if (!productOptions[productKey]) {
      setLoadingOptions({ ...loadingOptions, [productKey]: true });

      try {
        const requestData = {
          productId: product.productId,
          timePeriod,
          selectedBranchId: selectedBranch?.value || null
        };

        // Add time filters if they are set
        if (startTime && endTime) {
          requestData.startTime = startTime;
          requestData.endTime = endTime;
        }

        const response = await getProductOptionsBreakdown(requestData);

        if (response.success) {
          setProductOptions({
            ...productOptions,
            [productKey]: response.data.options
          });
          setExpandedProducts({ ...expandedProducts, [productKey]: true });
        } else {
          showSnackBar(response.message || 'Failed to fetch product options', 'error');
        }
      } catch (error) {
        console.error('Product options fetch error:', error);
        showSnackBar('Error fetching product options. Please try again.', 'error');
      } finally {
        setLoadingOptions({ ...loadingOptions, [productKey]: false });
      }
    } else {
      // We already have the data, just expand
      setExpandedProducts({ ...expandedProducts, [productKey]: true });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <FaSpinner className="animate-spin text-6xl text-indigo-600 mx-auto mb-4" />
          <p className="text-xl text-gray-700">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      {/* Snackbar */}
      {snackBar.show && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className={`fixed top-4 right-4 ${snackBar.type === 'success' ? 'bg-green-500' : 'bg-red-500'
            } text-white px-6 py-3 rounded-lg shadow-lg z-50`}
        >
          {snackBar.message}
        </motion.div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header Section with Clean Grid Layout */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-6">
          {/* Title */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <MdPointOfSale className="text-indigo-600" />
              Walk-In Analytics
            </h1>
          </div>

          {/* Main Controls Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            {/* Left Column: Filters */}
            <div className="lg:col-span-2 space-y-4">
              {/* Branch & Time Period Row */}
              <div className="flex flex-wrap gap-3">
                {/* Branch Selector */}
                {canSwitchBranch ? (
                  <div className="flex-1 min-w-[220px]">
                    <Select
                      value={selectedBranch}
                      onChange={(option) => {
                        setSelectedBranch(option);
                        setPagination({ ...pagination, currentPage: 1 });
                        setReturnsPagination((prev) => ({ ...prev, currentPage: 1 }));
                        setProductOptions({});
                        setExpandedProducts({});
                      }}
                      options={branchOptions}
                      placeholder="Select Branch"
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          backgroundColor: 'white',
                          border: state.isFocused ? '2px solid #6366f1' : '2px solid #d1d5db',
                          borderRadius: '8px',
                          padding: '2px 8px',
                          minHeight: '42px',
                          fontSize: '14px',
                          boxShadow: 'none',
                          cursor: 'pointer',
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: '#374151',
                        }),
                        menu: (provided) => ({
                          ...provided,
                          backgroundColor: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          zIndex: 9999,
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected ? '#6366f1' : state.isFocused ? '#eef2ff' : 'white',
                          color: state.isSelected ? 'white' : '#374151',
                          padding: '10px 12px',
                          cursor: 'pointer',
                          fontSize: '14px',
                        }),
                        indicatorSeparator: () => ({ display: 'none' }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          color: '#6b7280',
                        }),
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex-1 min-w-[220px] px-4 py-2 bg-white border-2 border-gray-300 rounded-lg text-sm font-medium text-gray-700">
                    Branch: {selectedBranch?.label || 'Loading...'}
                  </div>
                )}

                {/* Time Period Selector */}
                <div className="min-w-[180px]">
                  <Select
                    value={{ value: timePeriod, label: timePeriod === 'Weekly' ? 'This Week' : timePeriod === 'Monthly' ? 'This Month' : timePeriod }}
                    onChange={(option) => handleTimePeriodChange({ target: { value: option.value } })}
                    options={[
                      { value: "Today", label: "Today" },
                      { value: "Weekly", label: "This Week" },
                      { value: "Monthly", label: "This Month" }
                    ]}
                    formatOptionLabel={(option) => (
                      <div className="flex items-center">
                        {option.value === "Today" && <BsCalendarDay className="w-4 h-4 mr-2" />}
                        {option.value === "Weekly" && <BsCalendarWeek className="w-4 h-4 mr-2" />}
                        {option.value === "Monthly" && <BsCalendarMonth className="w-4 h-4 mr-2" />}
                        <span>{option.label}</span>
                      </div>
                    )}
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        backgroundColor: 'white',
                        border: state.isFocused ? '2px solid #6366f1' : '2px solid #d1d5db',
                        borderRadius: '8px',
                        padding: '2px 8px',
                        minHeight: '42px',
                        fontSize: '14px',
                        boxShadow: 'none',
                        cursor: 'pointer',
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#374151',
                        display: 'flex',
                        alignItems: 'center',
                      }),
                      menu: (provided) => ({
                        ...provided,
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        zIndex: 9999,
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#6366f1' : state.isFocused ? '#eef2ff' : 'white',
                        color: state.isSelected ? 'white' : '#374151',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }),
                      indicatorSeparator: () => ({ display: 'none' }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        color: '#6b7280',
                      }),
                    }}
                  />
                </div>
              </div>

              {/* Download Buttons Row */}
              <div className="flex flex-wrap gap-3">
                {/* Download PDF Button */}
                <button
                  onClick={handleDownloadPDF}
                  disabled={downloading}
                  className="flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-green-500 text-green-600 rounded-lg font-medium hover:bg-green-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {downloading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <FaDownload />
                      Download PDF
                    </>
                  )}
                </button>

                {/* Download Excel Button */}
                <button
                  onClick={handleDownloadExcel}
                  disabled={downloadingExcel}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-medium hover:from-emerald-600 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {downloadingExcel ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <FaFileExcel />
                      Download Excel (Tally)
                    </>
                  )}
                </button>
              </div>

              {/* Custom Date Range Download - Super Admin Only (role === 1) */}
              {isSuperOrMasterAdmin && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowCustomDateRange(!showCustomDateRange)}
                    className="flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
                  >
                    <BsCalendarMonth className="text-base" />
                    {showCustomDateRange ? 'Hide Custom Date Range' : 'Custom Date Range Download'}
                    {showCustomDateRange ? <FaChevronDown className="text-xs" /> : <FaChevronRight className="text-xs" />}
                  </button>

                  {showCustomDateRange && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-3 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border-2 border-indigo-200"
                    >
                      <p className="text-xs text-gray-600 mb-3">
                        Download data for any custom date range including archived/past months data (e.g., Dec 2025, Jan 2026)
                      </p>
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                            From Date
                          </label>
                          <input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            className="px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                            To Date
                          </label>
                          <input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            className="px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-indigo-500"
                          />
                        </div>
                        <button
                          onClick={handleCustomDateExcelDownload}
                          disabled={downloadingCustomExcel || !customStartDate || !customEndDate}
                          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-medium text-sm hover:from-emerald-600 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                        >
                          {downloadingCustomExcel ? (
                            <>
                              <FaSpinner className="animate-spin" />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <FaFileExcel />
                              Download Excel (Tally)
                            </>
                          )}
                        </button>
                        <button
                          onClick={handleCustomDatePDFDownload}
                          disabled={downloadingCustomPDF || !customStartDate || !customEndDate}
                          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-medium text-sm hover:from-red-600 hover:to-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                        >
                          {downloadingCustomPDF ? (
                            <>
                              <FaSpinner className="animate-spin" />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <FaFilePdf />
                              Download PDF
                            </>
                          )}
                        </button>
                      </div>
                      {customStartDate && customEndDate && (
                        <div className="mt-2 text-xs text-indigo-700 font-medium">
                          Range: {new Date(customStartDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} to {new Date(customEndDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              )}
            </div>

            {/* Right Column: GST Filing Workflow */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border-2 border-amber-200 p-5 h-full">
                <div className="flex items-start gap-3 mb-3">
                  <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-2 rounded-lg flex-shrink-0">
                    <FaFileExcel className="text-white text-lg" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-800">GST Filing</h3>
                    <p className="text-xs text-gray-600">Monthly Returns</p>
                  </div>
                </div>

                {/* <div className="space-y-2 mb-4">
                  <p className="text-xs text-gray-700 leading-relaxed">
                    <span className="font-semibold">Quick Steps:</span>
                  </p>
                  <ol className="text-xs text-gray-700 space-y-1 ml-1">
                    <li>1. Download Excel above</li>
                    <li>2. Make corrections</li>
                    <li>3. Upload modified file</li>
                    <li>4. Get GST JSON</li>
                  </ol>
                </div> */}

                <div className="space-y-2">
                  {/* Upload Excel Button */}
                  <label className="block cursor-pointer group">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleExcelUpload}
                      disabled={uploadingExcel}
                      className="hidden"
                    />
                    <div className={`flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg text-sm font-medium hover:from-blue-600 hover:to-blue-700 transition-all ${uploadingExcel ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer shadow-sm group-hover:shadow'}`}>
                      {uploadingExcel ? (
                        <>
                          <FaSpinner className="animate-spin text-sm" />
                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <FaFileExcel className="text-sm" />
                          <span>Upload Excel</span>
                        </>
                      )}
                    </div>
                  </label>

                  {/* Download GST JSON Button */}
                  <button
                    onClick={downloadGSTJson}
                    disabled={!uploadedExcelData || downloadingGSTJson}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg text-sm font-medium hover:from-purple-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow"
                  >
                    {downloadingGSTJson ? (
                      <>
                        <FaSpinner className="animate-spin text-sm" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <FaDownload className="text-sm" />
                        <span>Download JSON</span>
                      </>
                    )}
                  </button>

                  {/* Status indicator */}
                  {uploadedExcelData && (
                    <div className="flex items-center justify-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs font-medium text-green-700">Ready</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KPICard
            title="Total Revenue"
            value={formatIndianCurrency(kpis.totalRevenue)}
            icon={FaMoneyBillWave}
            color="border-l-green-600"
          />
          <KPICard
            title="Total Orders"
            value={kpis.totalOrders}
            icon={FaShoppingCart}
            color="border-l-blue-600"
          />
          <KPICard
            title="Avg Order Value"
            value={formatIndianCurrency(kpis.avgOrderValue)}
            icon={FaChartLine}
            color="border-l-purple-600"
          />
          <KPICard
            title="Items Sold"
            value={kpis.totalItemsSold}
            icon={FaTrophy}
            color="border-l-orange-600"
            subtitle={`Tax: ${formatIndianCurrency(kpis.totalTaxCollected)}`}
          />
        </div>

        {/* Payment Method Revenue Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KPICard
            title="Revenue on Cash"
            value={formatIndianCurrency(kpis.revenueOnCash || 0)}
            icon={FaMoneyBillWaveAlt}
            color="border-l-green-600"
            subtitle="Cash payments"
          />
          <KPICard
            title="Revenue on Card"
            value={formatIndianCurrency(kpis.revenueOnCard || 0)}
            icon={FaCreditCard}
            color="border-l-green-600"
            subtitle="Card payments"
          />
          <KPICard
            title="Revenue on UPI"
            value={formatIndianCurrency(kpis.revenueOnUPI || 0)}
            icon={FaMobileAlt}
            color="border-l-green-600"
            subtitle="UPI payments"
          />
          <KPICard
            title="Customers (mobile)"
            value={kpis.uniqueCustomers || 0}
            icon={FaUsers}
            color="border-l-indigo-600"
            subtitle={`${kpis.ordersWithMobile || 0} orders with mobile`}
          />
          {/* Coupon Discount KPI - Only show if there's discount */}
          {kpis.totalCouponDiscount > 0 && (
            <KPICard
              title="Total Coupon Discount"
              value={formatIndianCurrency(kpis.totalCouponDiscount)}
              icon={FaMoneyBill}
              color="border-l-red-600"
              subtitle="Savings given to customers"
            />
          )}
        </div>

        {/* Products Table Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-8"
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
                <button
                  type="button"
                  onClick={() => handleDetailTabChange('products')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                    detailTab === 'products'
                      ? 'bg-indigo-600 text-white shadow'
                      : 'text-slate-600 hover:bg-white'
                  }`}
                >
                  All Products
                </button>
                <button
                  type="button"
                  onClick={() => handleDetailTabChange('returns')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition inline-flex items-center gap-2 ${
                    detailTab === 'returns'
                      ? 'bg-rose-600 text-white shadow'
                      : 'text-slate-600 hover:bg-white'
                  }`}
                >
                  <FaUndo className="text-xs" /> Returned Items
                </button>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                {detailTab === 'products' ? 'All Products - Detailed View' : 'Returned Items'}
              </h2>
            </div>

            {/* Time Filter Toggle (Only for Today) */}
            {timePeriod === 'Today' && (
              <button
                onClick={handleTimeFilterToggle}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${showTimeFilter
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
              >
                {showTimeFilter ? 'Hide Time Filter' : 'Show Time Filter'}
              </button>
            )}
          </div>

          {/* Time Filter Section */}
          {showTimeFilter && timePeriod === 'Today' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 p-4 bg-indigo-50 rounded-lg border-2 border-indigo-200"
            >
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    Start Time
                  </label>
                  <Select
                    value={startTime ? { value: startTime, label: startTime } : null}
                    onChange={(option) => setStartTime(option ? option.value : '')}
                    options={timeSlots.map(slot => ({ value: slot, label: slot }))}
                    placeholder="Select start time"
                    isClearable
                    formatOptionLabel={(option) => (
                      <div className="flex items-center">
                        <BsClock className="w-4 h-4 mr-2" />
                        <span>{option.label}</span>
                      </div>
                    )}
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        backgroundColor: 'white',
                        border: state.isFocused ? '2px solid #6366f1' : '2px solid #d1d5db',
                        borderRadius: '8px',
                        padding: '2px 8px',
                        minHeight: '42px',
                        fontSize: '14px',
                        boxShadow: 'none',
                        cursor: 'pointer',
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#374151',
                        display: 'flex',
                        alignItems: 'center',
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: '#9ca3af',
                      }),
                      menu: (provided) => ({
                        ...provided,
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        zIndex: 9999,
                        maxHeight: '250px',
                      }),
                      menuList: (provided) => ({
                        ...provided,
                        maxHeight: '250px',
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#6366f1' : state.isFocused ? '#eef2ff' : 'white',
                        color: state.isSelected ? 'white' : '#374151',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }),
                      indicatorSeparator: () => ({ display: 'none' }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        color: '#6b7280',
                      }),
                    }}
                  />
                </div>

                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    End Time
                  </label>
                  <Select
                    value={endTime ? { value: endTime, label: endTime } : null}
                    onChange={(option) => setEndTime(option ? option.value : '')}
                    options={timeSlots.map(slot => ({ value: slot, label: slot }))}
                    placeholder="Select end time"
                    isClearable
                    formatOptionLabel={(option) => (
                      <div className="flex items-center">
                        <BsClock className="w-4 h-4 mr-2" />
                        <span>{option.label}</span>
                      </div>
                    )}
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        backgroundColor: 'white',
                        border: state.isFocused ? '2px solid #6366f1' : '2px solid #d1d5db',
                        borderRadius: '8px',
                        padding: '2px 8px',
                        minHeight: '42px',
                        fontSize: '14px',
                        boxShadow: 'none',
                        cursor: 'pointer',
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#374151',
                        display: 'flex',
                        alignItems: 'center',
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: '#9ca3af',
                      }),
                      menu: (provided) => ({
                        ...provided,
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        zIndex: 9999,
                        maxHeight: '250px',
                      }),
                      menuList: (provided) => ({
                        ...provided,
                        maxHeight: '250px',
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#6366f1' : state.isFocused ? '#eef2ff' : 'white',
                        color: state.isSelected ? 'white' : '#374151',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }),
                      indicatorSeparator: () => ({ display: 'none' }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        color: '#6b7280',
                      }),
                    }}
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleApplyTimeFilter}
                    disabled={!startTime || !endTime}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apply Filter
                  </button>

                  {(startTime || endTime) && (
                    <button
                      onClick={handleClearTimeFilter}
                      className="px-6 py-2 bg-gray-400 text-white rounded-lg font-semibold hover:bg-gray-500 transition-all"
                    >
                      Clear
                    </button>
                  )}

                  {startTime && endTime && (
                    <button
                      onClick={handleDownloadFilteredPDF}
                      disabled={downloadingFiltered}
                      className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-semibold hover:from-red-600 hover:to-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {downloadingFiltered ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <FaFilePdf />
                          Download Filtered PDF
                        </>
                      )}
                    </button>
                  )}

                  {startTime && endTime && (
                    <button
                      onClick={handleDownloadFilteredExcel}
                      disabled={downloadingFilteredExcel}
                      className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-semibold hover:from-teal-600 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {downloadingFilteredExcel ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <FaFileExcel />
                          Download Filtered Excel
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {startTime && endTime && (
                <div className="mt-3 text-sm text-indigo-700 font-medium">
                  {detailTab === 'products'
                    ? `Showing products sold between ${startTime} and ${endTime}`
                    : `Showing returns between ${startTime} and ${endTime}`}
                </div>
              )}
            </motion.div>
          )}

          {/* Search Filter */}
          <div className="mb-6">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={
                  detailTab === 'products'
                    ? 'Search products by name or ID...'
                    : 'Search by invoice, product, or mobile...'
                }
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
              />
            </div>
            {searchTerm && detailTab === 'products' && (
              <p className="mt-2 text-sm text-gray-600">
                Searching across all {pagination.totalProducts} products
              </p>
            )}
            {searchTerm && detailTab === 'returns' && (
              <p className="mt-2 text-sm text-gray-600">
                Searching across {returnsPagination.totalCount} returned lines
              </p>
            )}
          </div>

          {detailTab === 'returns' ? (
            <>
              {!returnsLoading && returnsSummary.totalLines > 0 && (
                <div className="mb-4 flex flex-wrap gap-3 text-sm">
                  <span className="px-3 py-1.5 rounded-lg bg-rose-50 text-rose-800 font-medium">
                    Refunded {formatIndianCurrency(returnsSummary.totalRefund)}
                  </span>
                  <span className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 font-medium">
                    {returnsSummary.totalQty} units
                  </span>
                  <span className="px-3 py-1.5 rounded-lg bg-amber-50 text-amber-800 font-medium">
                    Damaged {returnsSummary.damagedQty}
                  </span>
                  <span className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-800 font-medium">
                    Just return {returnsSummary.justReturnQty}
                  </span>
                </div>
              )}
              {returnsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <FaSpinner className="animate-spin text-4xl text-rose-600 mr-3" />
                  <p className="text-lg text-gray-700">Loading returned items...</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Option</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Refund</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Returned at</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {returnedItems.length > 0 ? (
                          returnedItems.map((row) => (
                            <tr key={String(row._id)} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 text-sm font-semibold text-slate-800 whitespace-nowrap">
                                {row.walkInOrderId || row.invoiceNumber || '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-800">
                                {row.productName}
                                {row.isFreeOfferItem && (
                                  <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">
                                    Free gift
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{row.optionLabel || '—'}</td>
                              <td className="px-4 py-3 text-sm text-right text-gray-800 font-medium">{row.quantity}</td>
                              <td className="px-4 py-3 text-sm">
                                <span
                                  className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                                    row.reason === 'damaged'
                                      ? 'bg-amber-100 text-amber-800'
                                      : 'bg-emerald-100 text-emerald-800'
                                  }`}
                                >
                                  {row.reasonLabel || (row.reason === 'damaged' ? 'Damaged' : 'Just return')}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-right font-semibold text-rose-700">
                                {formatIndianCurrency(row.refundAmount)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                {row.returnedAt
                                  ? new Date(row.returnedAt).toLocaleString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                  : '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {row.customerPhone || row.customerName || '—'}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="px-4 py-10 text-center text-gray-500">
                              {searchTerm
                                ? 'No returned items match your search.'
                                : 'No returned items for this period.'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {returnsPagination.totalPages > 1 && (
                    <div className="mt-6 flex flex-col md:flex-row items-center justify-between gap-4">
                      <div className="text-sm text-gray-600">
                        Showing page {returnsPagination.currentPage} of {returnsPagination.totalPages} ({returnsPagination.totalCount} returns)
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleReturnsPageChange(returnsPagination.currentPage - 1)}
                          disabled={!returnsPagination.hasPrevPage}
                          className="px-4 py-2 bg-white border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          onClick={() => handleReturnsPageChange(returnsPagination.currentPage + 1)}
                          disabled={!returnsPagination.hasNextPage}
                          className="px-4 py-2 bg-white border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          ) : productsLoading ? (
            <div className="flex items-center justify-center py-12">
              <FaSpinner className="animate-spin text-4xl text-indigo-600 mr-3" />
              <p className="text-lg text-gray-700">Loading products...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product ID</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Sale</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Orders</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Price</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {products.length > 0 ? (
                      products.map((product, index) => {
                        const productKey = product.productId;
                        const isExpanded = expandedProducts[productKey];
                        const options = productOptions[productKey] || [];
                        const isLoadingOptions = loadingOptions[productKey];

                        return (
                          <React.Fragment key={index}>
                            {/* Main Product Row */}
                            <tr className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-800 font-bold text-sm">
                                  {product.rank}
                                </span>
                              </td>
                              <td className="px-4 py-4">
                                <button
                                  onClick={() => handleProductClick(product)}
                                  className="flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline focus:outline-none transition-colors"
                                >
                                  {isExpanded ? (
                                    <FaChevronDown className="text-xs" />
                                  ) : (
                                    <FaChevronRight className="text-xs" />
                                  )}
                                  <span>{product.productName}</span>
                                  {isLoadingOptions && (
                                    <FaSpinner className="animate-spin text-xs ml-1" />
                                  )}
                                </button>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-700">{product.productId}</td>
                              <td className="px-4 py-4 text-sm text-right text-gray-900 font-semibold">
                                {formatIndianCurrency(product.revenue)}
                              </td>
                              <td className="px-4 py-4 text-sm text-right text-gray-700">{product.quantity}</td>
                              <td className="px-4 py-4 text-sm text-right text-gray-700">{product.orders}</td>
                              <td className="px-4 py-4 text-sm text-right text-gray-700">
                                {formatIndianCurrency(product.avgPrice)}
                              </td>
                            </tr>

                            {/* Expanded Options Rows */}
                            {isExpanded && options.length > 0 && (
                              options.map((option, optIdx) => (
                                <tr key={`${index}-opt-${optIdx}`} className="bg-indigo-50 border-l-4 border-indigo-400">
                                  <td className="px-4 py-3"></td>
                                  <td className="px-4 py-3 text-sm text-gray-700 pl-12">
                                    <span className="inline-flex items-center gap-2">
                                      <span className="text-indigo-600">→</span>
                                      <span className="font-medium">
                                        {typeof option.weight === 'object' && option.weight !== null
                                          ? option.weight.weightLabel || `${option.weight.weightValue} ${option.weight.metricLabel}`
                                          : option.weight}
                                      </span>
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-500 italic">Option #{optIdx + 1}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-800 font-medium">
                                    {formatIndianCurrency(option.revenue)}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-700">{option.quantity}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-700">{option.orders}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-700">
                                    {formatIndianCurrency(option.avgPrice)}
                                  </td>
                                </tr>
                              ))
                            )}

                            {/* No options message */}
                            {isExpanded && options.length === 0 && !isLoadingOptions && (
                              <tr className="bg-gray-50">
                                <td colSpan="7" className="px-4 py-3 text-sm text-center text-gray-500 italic">
                                  No sales data found for different product options
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          {searchTerm ? 'No products found matching your search.' : 'No products available.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                <div className="mt-6 flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-600">
                    Showing page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalProducts} total products)
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={!pagination.hasPrevPage}
                      className="px-4 py-2 bg-white border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      First
                    </button>

                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={!pagination.hasPrevPage}
                      className="px-4 py-2 bg-white border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      Previous
                    </button>

                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={i}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-4 py-2 rounded-lg font-medium transition-all ${pagination.currentPage === pageNum
                              ? 'bg-indigo-600 text-white'
                              : 'bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="px-4 py-2 bg-white border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      Next
                    </button>

                    <button
                      onClick={() => handlePageChange(pagination.totalPages)}
                      disabled={!pagination.hasNextPage}
                      className="px-4 py-2 bg-white border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default WalkInAnalytics;
